import {
  claimEditBaseSchema,
  claimEditSchema,
  createProviderSchema,
} from '@billing/validation';
import { factorySeeds } from './seeds/base';
import {
  integrationProvider,
  permanentPatient,
} from '../permanent-fixtures/permanent-integration';
import { z } from 'zod';
import { v4 } from 'uuid';

type FixtureRecord = Record<string, any>;
type CreateProviderPayload = z.infer<typeof createProviderSchema>;
export type ValidClaimCreatePayload = z.infer<typeof claimEditBaseSchema>;
export type ValidClaimEditPayload = z.infer<typeof claimEditSchema>;

const cloneFixture = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
};

const buildFromBase = <T extends FixtureRecord>(
  base: T,
  overrides: Partial<T> = {}
): T => ({ ...cloneFixture(base), ...cloneFixture(overrides) });

const hasOverride = <T extends FixtureRecord>(
  overrides: Partial<T>,
  key: keyof T
) => Object.prototype.hasOwnProperty.call(overrides, key);

export const buildOrganization = <
  T extends FixtureRecord = typeof factorySeeds.organization
>(
  overrides: Partial<T> = {}
) => buildFromBase(factorySeeds.organization, overrides);

export const buildRole = <T extends FixtureRecord = typeof factorySeeds.role>(
  overrides: Partial<T> = {}
) => buildFromBase(factorySeeds.role, overrides);

export const buildSite = <T extends FixtureRecord = typeof factorySeeds.site>(
  overrides: Partial<T> = {}
) => {
  const site = buildFromBase(factorySeeds.site, overrides);

  if (site.organization?.id && !hasOverride(overrides, 'organizationID')) {
    site.organizationID = site.organization.id;
  }

  return site;
};

export const buildUser = <T extends FixtureRecord = typeof factorySeeds.user>(
  overrides: Partial<T> = {}
) => {
  const user = buildFromBase(factorySeeds.user, overrides);

  if (user.organization?.id && !hasOverride(overrides, 'organizationID')) {
    user.organizationID = user.organization.id;
  }

  if (user.role?.id && !hasOverride(overrides, 'roleID')) {
    user.roleID = user.role.id;
  }

  if (user.providers?.[0]?.id && !hasOverride(overrides, 'providerID')) {
    user.providerID = user.providers[0].id;
  }

  return user;
};

export const buildProvider = <
  T extends FixtureRecord = typeof factorySeeds.provider
>(
  overrides: Partial<T> = {}
) => {
  const provider = buildFromBase(factorySeeds.provider, overrides);

  if (provider.user && provider.user.id && !overrides.userID) {
    provider.userID = provider.user.id;
  }

  if (provider.sites?.[0]?.id && !overrides.siteID) {
    provider.siteID = provider.sites[0].id;
  }

  return provider;
};

const baseLicenses = integrationProvider.licenses.filter(
  (
    license: (typeof integrationProvider.licenses)[0],
    index: number,
    licenses: typeof integrationProvider.licenses
  ) =>
    index ===
    licenses.findIndex(
      (candidate) =>
        candidate.payorID === license.payorID &&
        candidate.siteID === license.siteID &&
        candidate.license === license.license
    )
);

const baseCreateProviderPayload: Omit<CreateProviderPayload, 'id'> = {
  title: integrationProvider.title,
  firstName: integrationProvider.firstName,
  lastName: integrationProvider.lastName,
  email: integrationProvider.email,
  contactNote: integrationProvider.contactNote ?? null,
  specialtyCode: integrationProvider.specialtyCode,
  specialtyStartDate: integrationProvider.specialtyStartDate,
  siteID: integrationProvider.siteID,
  specialty: integrationProvider.specialty,
  licenses: baseLicenses,
  groups: [],
};

export const buildCreateProviderPayload = (
  overrides: Partial<CreateProviderPayload> = {}
): CreateProviderPayload => {
  return {
    id: v4(),
    ...baseCreateProviderPayload,
    ...overrides,
    licenses: overrides.licenses ?? [...baseCreateProviderPayload.licenses],
    groups: overrides.groups ?? [...baseCreateProviderPayload.groups],
  };
};

export const buildPatient = <
  T extends FixtureRecord = typeof factorySeeds.patient
>(
  overrides: Partial<T> = {}
) => {
  const patient = buildFromBase(factorySeeds.patient, overrides);

  if (patient.healthcards?.[0]?.id && !overrides.healthcardID) {
    patient.healthcardID = patient.healthcards[0].id;
  }

  return patient;
};

export const tempPatientFactory = (
  overrides: Partial<typeof permanentPatient> = {}
) => {
  return {
    ...permanentPatient,
    id: v4(),
    firstName: 'TempFirst',
    lastName: 'TempLast',
    ...overrides,
  };
};

export const buildHealthcard = <
  T extends FixtureRecord = typeof factorySeeds.healthcard
>(
  overrides: Partial<T> = {}
) => {
  const healthcard = buildFromBase(factorySeeds.healthcard, overrides);

  if (healthcard.patient?.id && !hasOverride(overrides, 'patientID')) {
    healthcard.patientID = healthcard.patient.id;
  }

  return healthcard;
};

export const buildStatus = <
  T extends FixtureRecord = typeof factorySeeds.status
>(
  overrides: Partial<T> = {}
) => buildFromBase(factorySeeds.status, overrides);

export const buildPayor = <T extends FixtureRecord = typeof factorySeeds.payor>(
  overrides: Partial<T> = {}
) => buildFromBase(factorySeeds.payor, overrides);

export const buildTariff = <
  T extends FixtureRecord = typeof factorySeeds.tariff
>(
  overrides: Partial<T> = {}
) => buildFromBase(factorySeeds.tariff, overrides);

export const buildDebit = <T extends FixtureRecord = typeof factorySeeds.debit>(
  overrides: Partial<T> = {}
) => {
  const debit = buildFromBase(factorySeeds.debit, overrides);

  if (debit.claim?.id && !hasOverride(overrides, 'claimID')) {
    debit.claimID = debit.claim.id;
  }

  if (debit.payor?.id && !hasOverride(overrides, 'payorID')) {
    debit.payorID = debit.payor.id;
  }

  if (debit.provider?.id && !hasOverride(overrides, 'providerID')) {
    debit.providerID = debit.provider.id;
  }

  if (debit.tariff?.id && !hasOverride(overrides, 'tariffID')) {
    debit.tariffID = debit.tariff.id;
  }

  debit.eobs = debit.eobs ?? [];

  return debit;
};

export const buildCredit = <
  T extends FixtureRecord = typeof factorySeeds.credit
>(
  overrides: Partial<T> = {}
) => {
  const credit = buildFromBase(factorySeeds.credit, overrides);

  if (credit.site?.id && !hasOverride(overrides, 'siteID')) {
    credit.siteID = credit.site.id;
  }

  if (credit.provider?.id && !hasOverride(overrides, 'providerID')) {
    credit.providerID = credit.provider.id;
  }

  return credit;
};

export const buildSubmission = <
  T extends FixtureRecord = typeof factorySeeds.submission
>(
  overrides: Partial<T> = {}
) => {
  const submission = buildFromBase(factorySeeds.submission, overrides);

  if (submission.site && submission.site.id && !overrides.siteID) {
    submission.siteID = submission.site.id;
  }

  if (submission.payor && submission.payor.id && !overrides.payorID) {
    submission.payorID = submission.payor.id;
  }

  return submission;
};

export const buildClaim = <T extends FixtureRecord = typeof factorySeeds.claim>(
  overrides: Partial<T> = {}
) => {
  const claim = buildFromBase(factorySeeds.claim, overrides);

  if (claim.site?.id && !overrides.siteID) {
    claim.siteID = claim.site.id;
  }

  if (claim.provider?.id && !overrides.providerID) {
    claim.providerID = claim.provider.id;
  }

  if (claim.patient?.id && !overrides.patientID) {
    claim.patientID = claim.patient.id;
  }

  if (claim.healthcard?.id && !overrides.healthcardID) {
    claim.healthcardID = claim.healthcard.id;
  }

  if (claim.payor?.id && !overrides.payorID) {
    claim.payorID = claim.payor.id;
  }

  if (claim.submissions?.length && !overrides.submissions) {
    claim.submissions = claim.submissions.map((submission: FixtureRecord) =>
      buildSubmission(submission)
    );
  }

  if (Array.isArray(claim.debits)) {
    claim.debits = claim.debits.map((debit: FixtureRecord) => ({
      ...debit,
      claimID: debit.claimID ?? claim.id ?? null,
      payorID: debit.payor?.id ?? claim.payor?.id ?? debit.payorID ?? null,
      providerID:
        debit.provider?.id ?? claim.provider?.id ?? debit.providerID ?? null,
      eobs: debit.eobs ?? [],
    })) as typeof claim.debits;
  }

  return claim;
};

const validClaimPayloadDefaults = {
  hospitalCode: '   ',
  hospitalName: '',
  serviceLocationIndicator: null,
  debits: [],
  statements: [],
} as const;

export const buildValidClaimCreatePayload = (
  overrides: Partial<ValidClaimCreatePayload> = {}
): ValidClaimCreatePayload =>
  claimEditBaseSchema.parse(
    buildClaim({
      ...validClaimPayloadDefaults,
      ...overrides,
    })
  );

export const buildValidClaimEditPayload = (
  overrides: Partial<ValidClaimEditPayload> = {}
): ValidClaimEditPayload =>
  claimEditSchema.parse(
    buildClaim({
      ...validClaimPayloadDefaults,
      ...overrides,
    })
  );
